import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  Phone, 
  Mail,
  Users,
  GraduationCap,
  Church
} from 'lucide-react';
import usePageBanner from '../hooks/usePageBanner';
import TranslatedContent from '../components/TranslatedContent';
import { AutoTranslate } from '../hooks/useAutoTranslate';
import { getParishPage } from '../services/api';

const TrosundervisningPage = () => {
  const { t, language, isRTL } = useLanguage();
  const { bannerImage } = usePageBanner('trosundervisning', language);
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await getParishPage('trosundervisning');
        if (data) {
          setCmsContent(data);
        }
      } catch (error) {
        // Using default content when CMS content not available
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Get dates from CMS or use defaults
  const trosTermName = cmsContent?.tros_term_name || 'vår-terminen 2026';
  
  const karlskronaDates = cmsContent?.karlskrona_dates?.length > 0 
    ? cmsContent.karlskrona_dates 
    : [
        '10 januari',
        '24 januari',
        '7 februari',
        '28 februari',
        '14 mars',
        '28 mars',
        '25 april',
        '9 maj',
        '23 maj',
        '6 juni – avslutning'
      ];
  
  const karlskronaTime = cmsContent?.karlskrona_time || 'kl. 10.00 till 12.00';

  const olofstromDates = cmsContent?.olofstrom_dates?.length > 0
    ? cmsContent.olofstrom_dates
    : [
        '10 januari',
        '24 januari',
        '14 februari',
        '28 februari',
        '14 mars',
        '28 mars',
        '18 april',
        '25 april',
        '9 maj',
        '23 maj – avslutning'
      ];
  
  const olofstromTime = cmsContent?.olofstrom_time || 'kl. 10.00 till 12.30';

  const vuxenDates = cmsContent?.vuxen_dates?.length > 0
    ? cmsContent.vuxen_dates
    : [
        '14 januari',
        '28 januari',
        '11 februari',
        '25 februari',
        '11 mars',
        '25 mars',
        '8 April',
        '22 April',
        '6 maj',
        '20 maj'
      ];
  
  const vuxenTime = cmsContent?.vuxen_time || 'Varannan tisdag, kl. 19.00';
  const vuxenTerm = cmsContent?.vuxen_term || 'Våren 2026';

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="relative h-72 overflow-hidden">
        <img
          src={bannerImage || 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=1920'}
          alt="Trosundervisning"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link 
              to="/churches" 
              className="inline-flex items-center text-yellow-400 hover:text-yellow-300 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <AutoTranslate>Tillbaka till Kyrkor</AutoTranslate>
            </Link>
            <h1 className="responsive-hero-title-sm font-serif font-bold text-white mb-4 fade-in">
              <AutoTranslate>Trosundervisning</AutoTranslate>
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              <AutoTranslate>Barn, ungdom och vuxenundervisning</AutoTranslate>
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Intro Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 mb-4">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
              <AutoTranslate>Datum för Trosundervisning {trosTermName}</AutoTranslate>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              <AutoTranslate>(Barn och ungdom)</AutoTranslate>
            </p>
          </div>

          {/* CMS Content (if available) */}
          {cmsContent && cmsContent.content && (
            <Card className="mb-12 border-0 shadow-xl overflow-hidden">
              <CardContent className="p-8">
                <TranslatedContent 
                  content={{ sv: cmsContent.content }} 
                  as="div" 
                  className="prose prose-lg max-w-none"
                  html
                />
              </CardContent>
            </Card>
          )}

          {/* Church Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            
            {/* Karlskrona */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-500 to-amber-600 p-6">
                <div className="flex items-center gap-3">
                  <Church className="w-8 h-8 text-white" />
                  <h3 className="text-2xl font-serif font-bold text-white">
                    Karlskrona
                  </h3>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-yellow-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">
                        <AutoTranslate>Datum:</AutoTranslate>
                      </p>
                      <ul className="space-y-1">
                        {karlskronaDates.map((date, idx) => (
                          <li key={`karlskrona-date-${date}-${idx}`} className="text-gray-700 flex items-center gap-2">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                            <AutoTranslate>{date}</AutoTranslate>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                    <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                    <p className="text-gray-700">
                      <span className="font-semibold"><AutoTranslate>Tid:</AutoTranslate></span>{' '}
                      <AutoTranslate>{karlskronaTime}</AutoTranslate>
                    </p>
                  </div>

                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <AutoTranslate>Om det finns förhinder skall föräldern meddela det till kateketen innan undervisningstillfället.</AutoTranslate>
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="font-semibold text-gray-900 mb-2">
                      <AutoTranslate>Kontakt:</AutoTranslate>
                    </p>
                    <div className="space-y-2">
                      <p className="text-gray-700"><AutoTranslate>Kyrkoherden</AutoTranslate></p>
                      <a 
                        href="mailto:katekes.karlskrona@fatimakrona.se"
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <Mail className="w-4 h-4" />
                        katekes.karlskrona@fatimakrona.se
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Olofström */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6">
                <div className="flex items-center gap-3">
                  <Church className="w-8 h-8 text-white" />
                  <h3 className="text-2xl font-serif font-bold text-white">
                    Olofström
                  </h3>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">
                        <AutoTranslate>Datum:</AutoTranslate>
                      </p>
                      <ul className="space-y-1">
                        {olofstromDates.map((date, idx) => (
                          <li key={`olofstrom-date-${date}-${idx}`} className="text-gray-700 flex items-center gap-2">
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            <AutoTranslate>{date}</AutoTranslate>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <p className="text-gray-700">
                      <span className="font-semibold"><AutoTranslate>Tid:</AutoTranslate></span>{' '}
                      <AutoTranslate>{olofstromTime}</AutoTranslate>
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="font-semibold text-gray-900 mb-2">
                      <AutoTranslate>Kontaktperson:</AutoTranslate>
                    </p>
                    <div className="space-y-2">
                      <p className="text-gray-700 font-medium">Jolanta Samwel</p>
                      <a 
                        href="tel:0705712708"
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <Phone className="w-4 h-4" />
                        0705 71 27 08
                      </a>
                      <a 
                        href="mailto:katekes.olofstrom@fatimakrona.se"
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <Mail className="w-4 h-4" />
                        katekes.olofstrom@fatimakrona.se
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Adult Education Section */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-violet-700 p-6">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-white" />
                <div>
                  <h3 className="text-2xl font-serif font-bold text-white">
                    <AutoTranslate>Konvertit- och vuxenundervisning</AutoTranslate>
                  </h3>
                  <p className="text-purple-100 text-sm mt-1">
                    <AutoTranslate>{vuxenTerm}</AutoTranslate>
                  </p>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <p className="text-gray-700 mb-6 leading-relaxed">
                <AutoTranslate>Fördjupning i Kyrkans katekes samt dess liv och spiritualitet för de som funderar på att konvertera eller för de som inte har konfirmerats och som redan är katoliker.</AutoTranslate>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-start gap-3 mb-4">
                    <Calendar className="w-5 h-5 text-purple-600 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">
                        <AutoTranslate>Aktuella datum:</AutoTranslate>
                      </p>
                      <ul className="space-y-1">
                        {vuxenDates.map((date, idx) => (
                          <li key={`vuxen-date-${date}-${idx}`} className="text-gray-700 flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                            <AutoTranslate>{date}</AutoTranslate>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <Clock className="w-5 h-5 text-purple-600 flex-shrink-0" />
                    <p className="text-gray-700">
                      <AutoTranslate>{vuxenTime}</AutoTranslate>
                    </p>
                  </div>
                  
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-800">
                      <AutoTranslate>Gruppen kommer att göra en pilgrimsresa (tillsammans med studentgruppen) till Polen på försommaren.</AutoTranslate>
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="font-semibold text-gray-900 mb-2">
                      <AutoTranslate>Kontakt:</AutoTranslate>
                    </p>
                    <div className="space-y-2">
                      <p className="text-gray-700 font-medium">Fr. Wojciech Waligórski</p>
                      <a 
                        href="tel:0706499143"
                        className="flex items-center gap-2 text-purple-600 hover:underline"
                      >
                        <Phone className="w-4 h-4" />
                        0706 49 91 43
                      </a>
                    </div>
                  </div>

                  <p className="text-purple-700 font-medium italic">
                    <AutoTranslate>Hjärtligt välkomna!</AutoTranslate>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrosundervisningPage;
